import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { addressService } from "../services/svc_addresses";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";

export const useTSQAddressList = (
  params: ListRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.address.list(params),
    queryFn: () => addressService.list(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

// export const useTSQAddressListV2 = (
//   params: ListRequestSchemaV2,
//   enabled: boolean = true
// ) => {
//   return useQuery({
//     queryKey: queryKeys.address.
//   })
// }

export const useTSQAddress = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.address.detail(id),
    queryFn: () => addressService.getById(id),
    enabled: !!id && enabled,
  });
};

export const useTSQWorldLocationsCountries = (enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.world.countries(),
    queryFn: () => addressService.getWorldLocationsCountries(),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24 * 7,
    enabled: enabled,
  });
};

export const useTSMCreateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addressService.createCountry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.world.countries() });
    },
    onError: (error) => {
      console.error("Failed to create Country:", error);
    },
  });
};

export const useTSQWorldLocationsStates = (enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.world.states(),
    queryFn: () => addressService.getWorldLocationsStates(),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24 * 7,
    enabled: enabled,
  });
};

export const useTSMCreateState = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addressService.createState,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.world.states() });
    },
    onError: (error) => {
      console.error("Failed to create State:", error);
    },
  });
};

export const useTSQWorldLocationsCities = (enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.world.cities(),
    queryFn: () => addressService.getWorldLocationsCities(),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24 * 7,
    enabled: enabled,
  });
};

export const useTSMCreateCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addressService.createCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.world.cities() });
    },
    onError: (error) => {
      console.error("Failed to create City:", error);
    },
  });
};

export const useTSMCreateAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addressService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.address.lists() });
    },
    onError: (error) => {
      console.error("Failed to create SO:", error);
    },
  });
};

export const useTSMUpdateAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      addressService.update(id, data),
    onSuccess: (updateAddress) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.address.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.address.detail(updateAddress.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update SO:", error);
    },
  });
};

export const useTSMDeactivateAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addressService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.address.lists() });
    },
    onError: (error) => {
      console.error("Failed to deactivate SO:", error);
    },
  });
};

export const useTSMReactivateAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addressService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.address.lists() });
    },
    onError: (error) => {
      console.error("Failed to reactivate SO:", error);
    },
  });
};
